import { IPD_ORDERS_CHANGED_EVENT, IPD_TASKS_CHANGED_EVENT, STAFF_ALERT_EVENT, STAFF_DUTY_EVENT } from '@his/shared';

export interface ChannelFamily {
  key?: string;
  keyed?: readonly string[];
  events: readonly string[] | null;
}

const CPOE_ORDERED = 'cpoe.ordered';
const CARE_TEAM_CHANGED = 'care-team.changed';
const QUEUE_EVENTS = ['queue.updated', 'appointment.created'];
const ADMISSION_KEYED = [IPD_ORDERS_CHANGED_EVENT, IPD_TASKS_CHANGED_EVENT, CARE_TEAM_CHANGED];
const EMAR_KEYED = [IPD_ORDERS_CHANGED_EVENT, CPOE_ORDERED, IPD_TASKS_CHANGED_EVENT];

export const CHANNEL_FAMILIES: Readonly<Record<string, ChannelFamily>> = {
  admission: { key: 'admissionId', keyed: ADMISSION_KEYED, events: [...ADMISSION_KEYED, 'lab.rejected', 'lab.critical', 'radiology.critical'] },
  emar: { key: 'admissionId', keyed: EMAR_KEYED, events: EMAR_KEYED },
  ward: { key: 'wardId', events: [IPD_TASKS_CHANGED_EVENT, STAFF_ALERT_EVENT] },
  user: {
    key: 'userId',
    events: [
      STAFF_ALERT_EVENT,
      STAFF_DUTY_EVENT,
      'lab.rejected',
      'lab.critical',
      'lab.reprint-decided',
      'radiology.retake',
      'radiology.critical',
      'radiology.second-opinion',
      'radiology.second-opinion-answered',
      'owner-watch.start',
      'owner-watch.end',
    ],
  },
  queue: { key: 'doctorId', events: QUEUE_EVENTS },
  'queue-branch': { key: 'branchId', events: [...QUEUE_EVENTS, 'calendar.changed'] },
  'queue-display': { events: QUEUE_EVENTS },
  'lab-worklist': {
    events: [
      'lab.ordered',
      'lab.cancelled',
      'lab.collected',
      'lab.received',
      'lab.rejected',
      'lab.verified',
      'lab.amended',
      'lab.sent-out',
      'lab.edit-requested',
      'lab.edit-decided',
      'lab.reprint-requested',
    ],
  },
  'lab-critical': { events: ['lab.critical'] },
  'radiology-worklist': { events: ['radiology.ordered', 'radiology.updated', 'radiology.released', 'radiology.retake'] },
  'radiology-critical': { events: ['radiology.critical'] },
  'bed-board': { events: ['bed.occupied', 'bed.transferred', 'bed.released', 'bed.updated', 'admission.consultant_changed'] },
  'rx-queue': { events: ['rx.issued', 'rx.dispensed'] },
  'er-board': { events: ['er.arrived', 'er.triaged', 'er.updated', 'er.disposed'] },
  cosign: { events: ['cosign.changed'] },
};

export function parseChannel(channel: string): { family: string; id: string | null } {
  const colon = channel.indexOf(':');
  return colon === -1 ? { family: channel, id: null } : { family: channel.slice(0, colon), id: channel.slice(colon + 1) };
}

export function channelListsEvent(channel: string, event: string, families: Readonly<Record<string, ChannelFamily>> = CHANNEL_FAMILIES): boolean {
  const spec = families[parseChannel(channel).family];
  return Boolean(spec?.events?.includes(event));
}

function payloadValue(payload: unknown, key: string | undefined): unknown {
  if (!key || !payload || typeof payload !== 'object') return undefined;
  return (payload as Record<string, unknown>)[key];
}

export interface DispatchFacts {
  explainedElsewhere: boolean;
  claimedByKeyed: boolean;
}

function keyedMatch(channel: string, event: string, payload: unknown, families: Readonly<Record<string, ChannelFamily>>): boolean {
  const { family, id } = parseChannel(channel);
  const spec = families[family];
  return id !== null && Boolean(spec?.keyed?.includes(event)) && payloadValue(payload, spec?.key) === id;
}

export function channelMayCarry(
  channel: string,
  event: string,
  payload: unknown,
  facts: DispatchFacts,
  families: Readonly<Record<string, ChannelFamily>> = CHANNEL_FAMILIES,
): boolean {
  const { family, id } = parseChannel(channel);
  const spec = families[family];
  if (!spec) return true;
  const value = payloadValue(payload, spec.key);
  if (id !== null && typeof value === 'string' && value !== id) return false;
  if (spec.events === null) return true;
  if (!spec.events.includes(event)) return !facts.explainedElsewhere;
  if (id !== null && spec.keyed?.includes(event)) return value === id;
  return !facts.claimedByKeyed;
}

export function channelsCarrying(subscribed: readonly string[], event: string, payload: unknown, families: Readonly<Record<string, ChannelFamily>> = CHANNEL_FAMILIES): Set<string> {
  const facts: DispatchFacts = {
    explainedElsewhere: subscribed.some((channel) => channelListsEvent(channel, event, families)),
    claimedByKeyed: subscribed.some((channel) => keyedMatch(channel, event, payload, families)),
  };
  return new Set(subscribed.filter((channel) => channelMayCarry(channel, event, payload, facts, families)));
}
